import { useState } from 'react';
import { Box, Popover, Typography, TextField, Grid } from '@mui/material';

const predefinedColors = [
  '#FF6B6B', // Red
  '#4CAF50', // Green
  '#FF9800', // Orange
  '#F44336', // Dark Red
  '#2196F3', // Blue
  '#9C27B0', // Purple
  '#00BCD4', // Cyan
  '#FFC107', // Amber
  '#673AB7', // Deep Purple
  '#E91E63', // Pink
  '#3F51B5', // Indigo
  '#009688', // Teal
  '#8BC34A', // Light Green
  '#FFEB3B', // Yellow
  '#795548', // Brown
  '#607D8B', // Blue Gray
];

const MultiColorPicker = ({ value, onChange, disabled = false }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [customColor, setCustomColor] = useState(value || '#FF6B6B');

  const handleClick = (event) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handlePredefinedColorSelect = (color) => {
    onChange({ target: { name: '', value: color } });
    setCustomColor(color);
    handleClose();
  };

  const handleCustomColorChange = (event) => {
    const newColor = event.target.value;
    setCustomColor(newColor);
    onChange({ target: { name: '', value: newColor } });
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Box
        onClick={handleClick}
        sx={{
          height: '48px',
          borderRadius: '8px',
          border: '1px solid #ccc',
          display: 'flex',
          alignItems: 'center',
          padding: '0 14px',
          cursor: disabled ? 'not-allowed' : 'pointer',
          backgroundColor: disabled ? '#f5f5f5' : '#FFFFFF',
          opacity: disabled ? 0.6 : 1,
          transition: 'all 0.2s',
          '&:hover': {
            borderColor: disabled ? '#ccc' : '#A5DCF3',
          },
        }}
      >
        <Box
          sx={{
            width: 24,
            height: 24,
            backgroundColor: value,
            borderRadius: '4px',
            marginRight: 1,
            border: '1px solid #999',
          }}
        />
        <Typography sx={{ fontSize: '14px' }}>{value}</Typography>
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box
          sx={{
            p: 2,
            minWidth: '300px',
            backgroundColor: '#FFFFFF',
          }}
        >
          {/* Custom Color Input */}
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: '12px', fontWeight: 600, mb: 1 }}>
              Custom Color
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <input
                type="color"
                value={customColor}
                onChange={handleCustomColorChange}
                style={{
                  width: '50px',
                  height: '40px',
                  borderRadius: '4px',
                  border: 'none',
                  cursor: 'pointer',
                }}
              />
              <TextField
                type="text"
                value={customColor.toUpperCase()}
                onChange={(e) => {
                  const val = e.target.value;
                  if (/^#[0-9A-F]{6}$/.test(val)) {
                    setCustomColor(val);
                    onChange({ target: { name: '', value: val } });
                  }
                }}
                size="small"
                placeholder="#RRGGBB"
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    fontSize: '12px',
                  },
                }}
              />
            </Box>
          </Box>

          {/* Predefined Colors */}
          <Box sx={{ mb: 1 }}>
            <Typography sx={{ fontSize: '12px', fontWeight: 600, mb: 1 }}>
              Predefined Colors
            </Typography>
            <Grid container spacing={0.5}>
              {predefinedColors.map((color) => (
                <Grid item xs={3} key={color}>
                  <Box
                    onClick={() => handlePredefinedColorSelect(color)}
                    sx={{
                      width: '100%',
                      aspectRatio: '1/1',
                      backgroundColor: color,
                      borderRadius: '4px',
                      cursor: 'pointer',
                      border: customColor === color ? '3px solid #000' : '1px solid #ccc',
                      '&:hover': {
                        transform: 'scale(1.05)',
                      },
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    title={color}
                  >
                    {customColor === color && (
                      <Typography sx={{ fontSize: '16px', color: '#fff', fontWeight: 'bold' }}>
                        ✓
                      </Typography>
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default MultiColorPicker;